import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory, compareArray } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { ensureArray, groupBy } from "../../common/utils/arrayUtilities";
import { dateRangeOverlap, dateSameOrAfter } from "../../common/utils/dateUtils";
import { ensureInteger } from "../../common/utils/generalUtils";
import { buildSearchQuery } from "../../utils/SearchScreenUtils";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = event => ensureInteger(event.id);

export const eventMgrSlice = createSlice({
    name: 'events',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(event => state.data[key(event)] = event);
        },
        setReplicas: (state, action) => {
            const replicationids = new Set(action.payload.data.map(e => e.replicationid));
            const toProcess = new Set(Object.values(state.data).filter(e => replicationids.has(e.replicationid)).map(e => key(e)));
            action.payload.data.forEach(e => {
                state.data[key(e)] = e;
                toProcess.delete(key(e));
            })
            toProcess.forEach(id => delete state.data[id]);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(e => state.data[key(e)] = e);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(event => delete state.data[event.id])
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => eventMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'event_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'event_one');

const fetchEvent = aggregatingFetch(
    id => network => network.get('/api/v1/event/' + encodeURIComponent(id), data => eventMgrSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/events', Array.from(ids), data => eventMgrSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);

const fetchTemplateEventsInRange = (templateid, start, end) => network => {
    const data = buildSearchQuery({templateid, start, end});
    let props = Object.keys(data);
    if (props.length === 0 || (props.length === 1 && data.status === '@')) {
        return [];
    }
    return network.post('/api/v1/eventSearch', data, eventMgrSlice.actions.addOrUpdate, visualizeFailure('get')); 
}

const fetchEventReplicas = (eventid, replicationid, editMode) => network => {
    return network.post('/api/v1/eventReplicas', { eventid, editMode }, data => eventMgrSlice.actions.setReplicas({ data, eventid }));
}
export const searchEvents = (network, search) => {
    const data = buildSearchQuery(search);
    let props = Object.keys(data);
    if (props.length === 0 || (props.length === 1 && data.status === '@')) {
        return [];
    }
    return network.post('/api/v1/eventSearch', data);
}

export const searchEventShifts = (network, search) => {
    const data = buildSearchQuery(search);
    let props = Object.keys(data);
    if (props.length === 0 || (props.length === 1 && data.status === '@')) {
        return [];
    }
    return network.post('/api/v1/shiftSearch', data, null, visualizeFailure('search'));
}

export const repeatEvent = (network, eventData) => {
    const { data, dates, copyRoster } = eventData;

    // Create a specific event repetition request with the dates
    if (ensureArray(dates) && dates.length > 0) {
        return network.post('/api/v1/eventReplicate', { templateid: data.id, dates, copyRoster: copyRoster ?? false }, visualizeSuccess('create', eventMgrSlice.actions.addOrUpdate), visualizeFailure('create'));
    }
    // Otherwise fallback to the standard replication (just one copy)
    return network.post('/api/v1/eventReplicate', data, visualizeSuccess('create', eventMgrSlice.actions.addOrUpdate), visualizeFailure('create'));
};
export const createEvent = (network, event) => network.post('/api/v1/event', event, visualizeSuccess('create', eventMgrSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateEvent = (network, delta) => network.put('/api/v1/event', delta, eventMgrSlice.actions.addOrUpdate, visualizeFailure('update'));
// #endregion

// #region selectors
const selectEventData = state => state.events.data;
const selectEvent = id => createSelector([selectEventData], events => events[id])
const selectEvents = ids => createSelector([selectEventData], events => ids.reduce((res, id) => {
    if (events[id]) {
        res.push(events[id]);
    }
    return res;
}, []));
const selectTemplateEventsInRange = (templateid, start, end) => createSelector([selectEventData], events => Object.values(events).filter(e => e.templateid == templateid && dateRangeOverlap(e, { start, end })));

const selectEventReplicaData = createSelector([selectEventData], events => groupBy(Object.values(events).filter(e => e.replicationid), 'replicationid'));
const selectEventReplicas = (id, replicationid, editMode) => createSelector([selectEventReplicaData], replicas => {
    if (editMode === 'all') {
        return replicas[replicationid] ?? [];
    } else if (editMode === 'none') {
        let event = replicas[replicationid]?.find(r => r.id === id);
        return  event ? [event] : [];
    } else if (editMode === 'future') {
        let baseEvent = replicas[replicationid]?.find(r => r.id === id);
        if (!baseEvent) {
            return [];
        }
        return replicas[replicationid].filter(e => dateSameOrAfter(baseEvent, e));
    } else {
        return [];
    }
});
// #endregion

const listen = {
    eventAdminCreated: (event, { dispatch, websocket }) => {
        markFetched(websocket, 'event', key(event))
        dispatch(eventMgrSlice.actions.addOrUpdate(event))
    },
    eventAdminUpdated: (event, { dispatch }) => dispatch(eventMgrSlice.actions.addOrUpdate(event)),
    eventAdminRemoved: (event, { dispatch }) => dispatch(eventMgrSlice.actions.remove(event))
}

// #region - permissions
// true: event with a template.id requires recurring access roles
// false: event with no template.id requires adhoc access roles
const eventAccess = new Map([
    ['fullRoles', new Map([
        [true, ['template manager', 'administrator']],
        [false, ['event manager', 'administrator']]
    ])],
    ['readonlyRoles', new Map([
        [true, ['auditor', 'template auditor', 'recurring rostering manager', 'recurring rostering auditor', 'equipment manager', 'equipment auditor', 'EPCR manager', 'EPCR auditor', 'timesheet auditor', 'timesheet manager']],
        [false, ['auditor', 'event auditor', 'rostering manager', 'rostering auditor', 'equipment manager', 'equipment auditor', 'EPCR manager', 'EPCR auditor', 'timesheet auditor', 'timesheet manager']]
    ])],
    ['unrestrictedRoles', new Map([
        [true, ['template manager', 'template auditor', 'administrator', 'auditor']],
        [false, ['event manager', 'event auditor', 'administrator', 'auditor']]
    ])],
]);

const eventHoursRoles = ['administrator', 'rostering manager', 'recurring rostering manager', 'auditor', 'rostering auditor', 'recurring rostering auditor']

const checkPermissions = (credentials, event, requiredRoles, condition = true) => {
    return (
        credentials.hasRole(requiredRoles, { office: event?.office, client: event?.client, location: event?.locationid }) &&
        condition
    );
};

export const canCreateEvent = (credentials, event) => event ? checkPermissions(credentials, event, eventAccess.get('fullRoles').get(!!event?.templateid)) : credentials.hasRole(eventAccess.get('fullRoles').get(false));
export const canUpdateEvent = canCreateEvent;
export const canRemoveEvent = canCreateEvent;
export const canViewFullEvent = (credentials, event) => checkPermissions(credentials, event, eventAccess.get('unrestrictedRoles').get(!!event?.templateid));

export const canMgrViewEvents = (credentials) => {
    let fullRoles = eventAccess.get('fullRoles').get(true).concat(eventAccess.get('fullRoles').get(false));
    let readRoles = eventAccess.get('readonlyRoles').get(true).concat(eventAccess.get('readonlyRoles').get(false));
    return credentials.hasRole(fullRoles.concat(readRoles));
}
// #endregion

export const canRouteEvents = ({credentials, clientConfig}) => canMgrViewEvents(credentials) && !clientConfig.epcrOnly ;
export const canRouteEventReports = ({credentials}) => credentials.hasRole(eventAccess.get('unrestrictedRoles').get(true).concat(eventAccess.get('unrestrictedRoles').get(false)));
export const canRouteEventHours = ({credentials}) => credentials.hasRole(eventHoursRoles);

// have to use the same factory for the single and multiple gets so that single and multiple deps are unified
const eventInitByID = initMapFactory(fetchEvent, listen, { name: 'eventByID'});
registerEntityBasedFetch('event', eventInitByID);

const cleanup = { root: eventMgrSlice.name, all: state => Object.values(selectEventData(state)), removeAll: eventMgrSlice.actions.remove, key };
export const [useMgrEvent, realizeMgrEvent] = dataHookWithCredentialsFactory(selectEvent, eventInitByID, { condition: canMgrViewEvents, cleanup, defaultValue: null });
export const [useMgrEvents] = dataHookWithCredentialsFactory(selectEvents, initMultiMapFactory(eventInitByID, listen), { condition: canMgrViewEvents, cleanup, comparator: compareArray });
export const realizeMgrEvents = async (context, ids) => await Promise.all(ids.map(id => realizeMgrEvent(context, id)));
export const [useEventReplicas] = dataHookFactory(selectEventReplicas, queryMapFactory(fetchEventReplicas, 'id', eventInitByID, listen, { isEmpty: args => args.length === 0 || args[0] === null || args[0] === undefined}), { cleanup, comparator: compareArray });
export const [useMgrTemplateEventsInRange] = dataHookWithCredentialsFactory(selectTemplateEventsInRange, queryMapFactory(fetchTemplateEventsInRange, 'id', eventInitByID, listen), { condition: canMgrViewEvents, cleanup, defaultValue: null, comparator: compareArray });


export const defaultEvent = (clientConfig) => {
    let event = {};
    
    if (clientConfig.eventStatus) {
        event.status = clientConfig.eventStatus[0].name;
    }
    
    if (clientConfig.defaultOffice) {
        event.office = [clientConfig.defaultOffice];
    }
    
    if (clientConfig.tz) {
        event.tz = clientConfig.tz;
    }

    if (clientConfig.useBrands && clientConfig.defaultBrand) {
        event.brand = clientConfig.defaultBrand;
    }

    event.customfields = {};
    event.features = {};
    
    return event;
};

const reducer = { name: eventMgrSlice.name, reducer: eventMgrSlice.reducer }
export default reducer;